import Grid from '@mui/material/Grid';
import Box from '@mui/material/Box';
import { styled } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import ErrorIcon from '@mui/icons-material/Error';
import React, { CSSProperties, PropsWithChildren, ReactNode, useEffect, useState } from 'react';

import QRCode from 'react-qr-code';
import { MOBILE_SCREEN_SIZE, TABLET_SCREEN_SIZE } from '../../../utils/constants';
import _get from 'lodash/get';
import { brandclub_colors } from '../../../colors';
import { Typography } from '@mui/material';
import { ButtonLoadingEllipsis } from '../Loading';
import { Button } from '../../../Components/Button';
import { UserProfile } from '@brandclub/types';
import { formatPhoneNumber } from '../../../utils';

type SendStatus = 'normal' | 'sending' | 'sent' | 'failed';

const QRCodeContainer = styled(Grid)({
  [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
    display: 'none',
  },
});

export enum BCPhoneInputVariant {
  DEFAULT = 'default',
  XS = 'xs',
  SM = 'sm',
  DRAWER = 'drawer',
  DRAWER_MOBILE = 'drawer-mobile',
}

const variantStyles: Record<BCPhoneInputVariant, Record<string, CSSProperties>> = {
  [BCPhoneInputVariant.DEFAULT]: {
    container: {
      maxWidth: '100%',
    },
    inputContainer: {
      height: 56,
      minWidth: 345,
      borderWidth: 1.5,
    },
    input: {
      fontSize: 20,
      margin: '0 0 0 10px',
    },
    prefix: {
      fontSize: 18,
      marginLeft: 20,
    },
    button: {
      fontSize: 18,
      width: 134,
      height: 46,
    },
  },
  [BCPhoneInputVariant.XS]: {
    container: {
      maxWidth: 269,
    },
    inputContainer: {
      height: 39.1,
      minWidth: 269,
      borderWidth: 1,
    },
    input: {
      fontSize: 14,
      margin: '0 0 0 7px',
    },
    prefix: {
      fontSize: 14,
      marginLeft: 7,
    },
    button: {
      fontSize: 14,
      width: 91,
      height: 29,
    },
  },
  [BCPhoneInputVariant.SM]: {
    container: {
      maxWidth: 314,
    },
    inputContainer: {
      height: 46.4,
      minWidth: 314,
      borderWidth: 1.5,
    },
    input: {
      fontSize: 14,
      margin: '0 0 0 7px',
    },
    prefix: {
      fontSize: 14,
      marginLeft: 7,
    },
    button: {
      fontSize: 14,
      width: 106,
      height: 35,
    },
  },
  [BCPhoneInputVariant.DRAWER]: {
    container: {
      maxWidth: 345,
    },
    inputContainer: {
      height: 55,
      minWidth: 345,
      borderWidth: 1.5,
    },
    input: {
      fontSize: 16,
      margin: '0 0 0 10px',
    },
    prefix: {
      fontSize: 16,
      marginLeft: 20,
    },
    button: {
      fontSize: 16,
      width: 134,
      height: 46,
    },
  },
  [BCPhoneInputVariant.DRAWER_MOBILE]: {
    container: {
      maxWidth: 288,
    },
    inputContainer: {
      height: 49,
      minWidth: 288,
      borderWidth: 1.5,

      padding: '0 3px',
    },
    input: {
      fontSize: 14,
      margin: '0 0 0 7px',
    },
    prefix: {
      fontSize: 14,
      marginLeft: 7,
    },
    button: {
      fontSize: 14,
      width: 80,
      height: 40,
    },
  },
} as const;

const InputContainer = styled(Grid, {
  shouldForwardProp: (prop) => !['variant'].includes(prop.toString()),
})<{ variant: BCPhoneInputVariant }>(({ variant, theme }) => {
  const variantStyle = variantStyles[variant];
  return {
    width: '100%',
    position: 'relative',
    ...variantStyle.container,
    '.textme_header': {
      fontWeight: 600,
      color: theme.palette.primary.main,
      fontSize: 18,
      marginBottom: 10,
      [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
        display: 'none',
      },
    },
    '.textme_input': {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'flex-end',
      borderStyle: 'solid',
      borderColor: theme.palette.primary.main,
      borderRadius: 100,
      backgroundColor: '#fff',
      padding: '0 5px',
      maxWidth: 400,
      width: '100%',
      boxSizing: 'border-box',
      ...variantStyle.inputContainer,
      '.text_prefix': {
        color: theme.palette.primary.main,
        fontWeight: 500,
        ...variantStyle.prefix,
        '&.sent': {
          display: 'none',
        },
      },
      input: {
        fontFamily: 'Poppins',
        width: 0,
        flex: 1,
        color: theme.palette.primary.main,
        backgroundColor: '#fff',
        fontWeight: 500,
        border: 0,
        outline: 'none',
        ...variantStyle.input,
        '&.sent': {
          display: 'none',
        },
        '&::placeholder': {
          color: '#a7afc4',
        },
      },
      '.text_button': {
        cursor: 'pointer',
        fontWeight: 500,
        marginRight: 0,
        border: 0,
        backgroundColor: theme.palette.primary.main,
        color: '#fff',
        borderRadius: 100,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'all 0.2s ease',
        ...variantStyle.button,
        '&.sent': {
          width: '100%',
        },
      },
    },
    '.textme_button_group': {
      display: 'none',
      [`@media screen and (max-width: ${TABLET_SCREEN_SIZE}px)`]: {
        display: 'flex',
        marginTop: 15,
        gap: '10px',
        button: {
          borderWidth: 1,
          borderStyle: 'solid',
          outline: 'none',
          borderRadius: 100,
          height: 40,
          fontSize: 16,
          '&.download': {
            borderColor: theme.palette.primary.main,
            color: theme.palette.primary.main,
            background: 'rgba(158,224,208,.2)',
            flex: 1,
          },
          '&.text_link': {
            flex: 1,
            borderColor: theme.palette.primary.main,
            color: brandclub_colors.white,
            background: theme.palette.primary.main,
          },
        },
      },
    },
  };
});

const ErrorMessage = ({ children, ...props }: PropsWithChildren) => {
  if (!children) {
    return null;
  }
  const lineHeight = 1.5;
  return (
    <Typography
      {...props}
      sx={{
        color: '#ff534a',
        backgroundColor: 'rgba(255, 83, 74, 0.05)', // #ff534a with 5% opacity
        fontSize: 14,
        marginTop: 2.5,
        padding: '7px 12px 7px 9px',
        display: 'flex',
        alignItems: 'flex-start',
        width: 'max-content',
        borderRadius: 1,
        fontWeight: 600,
        lineHeight,
      }}
    >
      <Box component="span" display="flex" alignItems="center" height={`${lineHeight}em`}>
        <ErrorIcon sx={{ paddingRight: 0.75, fontSize: 18 }} />
      </Box>
      {children}
    </Typography>
  );
};

const getSendStatusMessage = (sendStatus: SendStatus, defaultMessage: string): ReactNode => {
  switch (sendStatus) {
    case 'sending':
      return (
        <>
          Sending
          <ButtonLoadingEllipsis baseFontSize={17}></ButtonLoadingEllipsis>
        </>
      );
    case 'sent':
      return 'Text sent!';
    default:
      return defaultMessage;
  }
};

export interface BrandclubPhoneInputProps {
  handleSendText: (phoneNumber: string) => void;
  qrCodeUrl?: string;
  hasHeader?: boolean;
  buttonText?: string;
  variant?: BCPhoneInputVariant;
  onTextSent?: () => void;
  userProfile: UserProfile | null | undefined;
}

const BrandclubPhoneInput = ({
  handleSendText,
  qrCodeUrl,
  hasHeader = true,
  buttonText = 'Send Text',
  variant,
  onTextSent,
  userProfile,
}: BrandclubPhoneInputProps) => {
  const [phoneNumber, setPhoneNumber] = useState('');
  const [sendStatus, setSendStatus] = useState<'normal' | 'sending' | 'sent' | 'failed'>('normal');
  const desktopView = useMediaQuery(`(min-width:${TABLET_SCREEN_SIZE}px)`);
  const mobileView = useMediaQuery(`(max-width:${MOBILE_SCREEN_SIZE}px)`);
  const isSignedIn = !!userProfile;
  const userPhoneNumber = userProfile?.phoneNumber;
  const variantToUse = variant
    ? variant
    : desktopView
    ? BCPhoneInputVariant.DEFAULT
    : mobileView
    ? BCPhoneInputVariant.XS
    : BCPhoneInputVariant.SM;

  useEffect(() => {
    if (sendStatus === 'sent') {
      // Reset the send status after 3 seconds
      setTimeout(() => {
        setSendStatus('normal');
        setPhoneNumber('');
      }, 3000);
    }
  }, [sendStatus]);

  const sendText = async () => {
    try {
      let number = userPhoneNumber ?? phoneNumber;
      number = number.replace(/\D/g, ''); // removes all non-digit characters
      setSendStatus('sending');
      const res = await handleSendText(number);
      const sentStatus = _get(res, ['waitListEntry', 'textInvitationStatus']);
      if (sentStatus === 'sent') {
        if (onTextSent) onTextSent();
        setSendStatus('sent');
      } else {
        setSendStatus('failed');
      }
    } catch (e) {
      setSendStatus('failed');
    }
  };

  return (
    <Box display="flex" flexDirection="column">
      {qrCodeUrl && (
        <QRCodeContainer>
          <QRCode value={qrCodeUrl} fgColor="#181d56" size={95} />
        </QRCodeContainer>
      )}
      <InputContainer container flexDirection={'column'} variant={variantToUse}>
        {hasHeader && <div className="textme_header">Download the app to access rewards</div>}
        {isSignedIn && userPhoneNumber ? (
          <Button
            sx={{
              height: 50,
              maxWidth: 355,
              width: '100%',
              fontSize: 16,
              alignSelf: 'center',
              [`@media (max-width: ${MOBILE_SCREEN_SIZE}px)`]: {
                height: 40,
                fontSize: 12,
                maxWidth: 300,
              },
            }}
            onClick={['normal', 'failed'].includes(sendStatus) ? sendText : undefined}
            disabled={sendStatus === 'sending'}
          >
            {getSendStatusMessage(sendStatus, `Text link to ${formatPhoneNumber(userPhoneNumber)}`)}
          </Button>
        ) : (
          <div className={`textme_input`}>
            <div className={`text_prefix ${sendStatus}`}>+1</div>
            <input
              className={`phone_input ${sendStatus}`}
              name="phone_number"
              placeholder="Phone number"
              value={phoneNumber}
              type="tel"
              onChange={(e) => {
                setSendStatus('normal');
                setPhoneNumber(e.target.value);
              }}
            ></input>
            <Button
              className={`text_button ${sendStatus}`}
              style={{
                minWidth: 'max-content',
              }}
              onClick={['normal', 'failed'].includes(sendStatus) ? sendText : undefined}
              disabled={sendStatus === 'sending'}
            >
              {getSendStatusMessage(sendStatus, buttonText)}
            </Button>
          </div>
        )}
      </InputContainer>
      <ErrorMessage>{sendStatus === 'failed' && 'Failed to send text. Please try again.'}</ErrorMessage>
    </Box>
  );
};

export default BrandclubPhoneInput;
